import { useGetAccountStates } from "@/api/account-state/account-state.ts"
import { useGetAccount } from "@/api/account/account.ts"
import { useGetFeatureFlags } from "@/api/config/config.ts"
import {
  FlexMarketSubscriptionState,
  MeterAppointmentState,
  SmartChargingState,
  SmartPayAsYouGoState,
  SupplyState,
} from "@/api/model"
import { useAuthSession } from "@/authentication/useAuthSession.ts"
import { aggregateQueries } from "@/lib/aggregateQueries.ts"
import nativeHelper from "@/lib/nativeHelper.ts"
import { isClosureSequencePlaying } from "@/lib/shared/closureSequence.ts"
import { useGetAccountId } from "@/lib/useGetAccountId.ts"
import { computed, Ref } from "vue"

export interface NavConfig {
  isAuthenticated: boolean
  isWHDEligible: boolean
  isTraditionalSmartEligible: boolean
  isAccountClosed: boolean
  isTerminating: boolean
  isLeaving: boolean
  hasMoveOut: boolean
  isSmartPayAsYouGoOn: boolean
  hasFlexMarketSubscription: boolean
  showSoCharged: boolean
  isSmartChargingEnrolled: boolean
  isMoveWithUsEnabled: boolean
}

export function useNavConfig(): {
  isLoading: Ref<boolean>
  navConfig: Ref<NavConfig>
} {
  const { isAuthenticated } = useAuthSession()

  const customerQuery = useGetAccountId()
  const accountQuery = useGetAccount(customerQuery.accountId)
  const accountStatesQuery = useGetAccountStates(customerQuery.accountId)
  const featureFlagsQuery = useGetFeatureFlags({
    query: {
      enabled: isAuthenticated,
    },
  })
  const { isLoading } = aggregateQueries([
    customerQuery,
    accountQuery,
    accountStatesQuery,
    featureFlagsQuery,
  ])

  const isAccountClosed = computed(
    () =>
      !!accountQuery.data.value?.isClosed && !isClosureSequencePlaying.value,
  )

  const isTerminating = computed(() => !!accountQuery.data.value?.isTerminating)

  const isLeaving = computed(() => {
    const account = accountQuery.data.value
    return !!account?.isClosed || !!account?.isTerminating
  })
  const isWHDEligible = computed(
    () =>
      !!featureFlagsQuery.data.value?.warmHomeDiscount &&
      !!accountQuery.data.value?.isWHDEligible,
  )

  const isTraditionalSmartEligible = computed(
    () =>
      accountStatesQuery.data.value?.meterAppointment ===
      MeterAppointmentState.traditionalSmartEligible,
  )
  const hasMoveOut = computed(
    () =>
      accountStatesQuery.data.value?.supply === SupplyState.movedOut ||
      accountStatesQuery.data.value?.supply === SupplyState.futureMoveOut,
  )

  const isSmartPayAsYouGoOn = computed(
    () =>
      accountStatesQuery.data.value?.smartPayAsYouGo ===
      SmartPayAsYouGoState.on,
  )

  const hasFlexMarketSubscription = computed(() => {
    const state = accountStatesQuery.data.value?.flexMarketSubscription
    return (
      state === FlexMarketSubscriptionState.active ||
      state === FlexMarketSubscriptionState.activeNoAssets
    )
  })

  const showSoCharged = computed(() => {
    const flags = featureFlagsQuery.data.value
    if (!flags) return false
    return nativeHelper.isNative()
      ? flags.showSoChargedMobileApp
      : flags.showSoChargedWeb
  })

  const isSmartChargingEnrolled = computed(
    () =>
      accountStatesQuery.data.value?.smartCharging ===
      SmartChargingState.enrolled,
  )

  const isMoveWithUsEnabled = computed(
    () => !!featureFlagsQuery.data.value?.cotStayUpfront,
  )

  const navConfig = computed(() => ({
    isAuthenticated: isAuthenticated.value,
    isWHDEligible: isWHDEligible.value,
    isTraditionalSmartEligible: isTraditionalSmartEligible.value,
    isAccountClosed: isAccountClosed.value,
    isTerminating: isTerminating.value,
    isLeaving: isLeaving.value,
    hasMoveOut: hasMoveOut.value,
    isSmartPayAsYouGoOn: isSmartPayAsYouGoOn.value,
    hasFlexMarketSubscription: hasFlexMarketSubscription.value,
    showSoCharged: showSoCharged.value,
    isSmartChargingEnrolled: isSmartChargingEnrolled.value,
    isMoveWithUsEnabled: isMoveWithUsEnabled.value,
  }))

  return { isLoading, navConfig }
}
